/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { AnnouncementActivityTaskReq } from './announcement-activity-task-req'
import { AnnouncementActivityTaskRes } from './announcement-activity-task-res'
import { TaskReflection } from './task-reflection'
import { LoadAnnouncementActivityTaskQuery } from './load-announcement-activity-task-query'
import { QueryAnnouncementReflectionsQuery } from './query-announcement-reflections-query'
import { ExportAnnouncementTaskReflectionsQuery } from './export-announcement-task-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const announcementApi = {
  createAnnouncementActivityTaskConfig(
    announcementActivityTaskReq: AnnouncementActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/announcement/activity-tasks',
      {},
      null,
      announcementActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】公告-创建活动任务
   * @param announcementActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  createAnnouncementActivityTask(
    announcementActivityTaskReq: AnnouncementActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      announcementApi.createAnnouncementActivityTaskConfig(
        announcementActivityTaskReq,
        options
      )
    ) as any
  },

  useCreateAnnouncementActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: AnnouncementActivityTaskReq) =>
        announcementApi.createAnnouncementActivityTask(payload),
      mutationOptions
    )
  },

  updateAnnouncementActivityTaskConfig(
    id: number,
    announcementActivityTaskReq: AnnouncementActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/announcement/activity-tasks/{id}',
      {
        id,
      },
      null,
      announcementActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】公告-编辑活动任务
   * @param id
   * @param announcementActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  updateAnnouncementActivityTask(
    id: number,
    announcementActivityTaskReq: AnnouncementActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      announcementApi.updateAnnouncementActivityTaskConfig(
        id,
        announcementActivityTaskReq,
        options
      )
    ) as any
  },

  useUpdateAnnouncementActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: AnnouncementActivityTaskReq }) =>
        announcementApi.updateAnnouncementActivityTask(id, payload),
      mutationOptions
    )
  },

  loadAnnouncementActivityTaskConfig(
    id: number,
    loadAnnouncementActivityTaskQuery?: LoadAnnouncementActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/announcement/activity-tasks/{id}',
      {
        id,
      },
      loadAnnouncementActivityTaskQuery
    )
  },

  /**
   * @summary 【前/后台】公告-详情
   * @param id
   * @param [loadAnnouncementActivityTaskQuery]
   * @param [loadAnnouncementActivityTaskQuery.isForeground] 是否是前台请求
   * @param [options]
   * @return AxiosResponsePromise<AnnouncementActivityTaskRes>
   */
  loadAnnouncementActivityTask(
    id: number,
    loadAnnouncementActivityTaskQuery?: LoadAnnouncementActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<AnnouncementActivityTaskRes> {
    return axios(
      announcementApi.loadAnnouncementActivityTaskConfig(
        id,
        loadAnnouncementActivityTaskQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new AnnouncementActivityTaskRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadAnnouncementActivityTask(
    {
      id,
      query,
    }: {
      id: number
      query?: LoadAnnouncementActivityTaskQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<AnnouncementActivityTaskRes> {
    return useQueryApi<AnnouncementActivityTaskRes>(
      announcementApi.loadAnnouncementActivityTask,
      'Announcement.loadAnnouncementActivityTask',
      queryOptions,
      id,
      query
    )
  },

  completeAnnouncementActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/announcement/activity-tasks/{id}/complete',
      {
        id,
      }
    )
  },

  /**
   * @summary 【前台】公告-完成活动任务
   *  - 添加完成人到任务人员列表
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  completeAnnouncementActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      announcementApi.completeAnnouncementActivityTaskConfig(id, options)
    ) as any
  },

  useCompleteAnnouncementActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => announcementApi.completeAnnouncementActivityTask(id),
      mutationOptions
    )
  },

  checkAnnouncementTaskIsSubmitConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/announcement/activity-tasks/{id}/is-submit',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】公告-查询是否已有学生完成
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  checkAnnouncementTaskIsSubmit(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      announcementApi.checkAnnouncementTaskIsSubmitConfig(id, options)
    ) as any
  },

  useCheckAnnouncementTaskIsSubmit(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<boolean> {
    return useQueryApi<boolean>(
      announcementApi.checkAnnouncementTaskIsSubmit,
      'Announcement.checkAnnouncementTaskIsSubmit',
      queryOptions,
      id
    )
  },

  queryAnnouncementReflectionsConfig(
    id: number,
    queryAnnouncementReflectionsQuery?: QueryAnnouncementReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/announcement/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryAnnouncementReflectionsQuery
    )
  },

  /**
   * @summary 【后台】公告任务-公告数据-任务人员列表(完成情况)
   * @param id
   * @param [queryAnnouncementReflectionsQuery]
   * @param [queryAnnouncementReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryAnnouncementReflectionsQuery.reflection.nameIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.nameLike]
   * @param [queryAnnouncementReflectionsQuery.reflection.accountLike]
   * @param [queryAnnouncementReflectionsQuery.reflection.accountIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.genderIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.statusIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.phoneIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.phoneLike]
   * @param [queryAnnouncementReflectionsQuery.reflection.emailIn]
   * @param [queryAnnouncementReflectionsQuery.reflection.emailLike]
   * @param [queryAnnouncementReflectionsQuery.sort]
   * @param [queryAnnouncementReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryAnnouncementReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryAnnouncementReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryAnnouncementReflections(
    id: number,
    queryAnnouncementReflectionsQuery?: QueryAnnouncementReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<TaskReflection[]> {
    return axios(
      announcementApi.queryAnnouncementReflectionsConfig(
        id,
        queryAnnouncementReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryAnnouncementReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: QueryAnnouncementReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<TaskReflection[]> {
    return useQueryApi<TaskReflection[]>(
      announcementApi.queryAnnouncementReflections,
      'Announcement.queryAnnouncementReflections',
      queryOptions,
      id,
      query
    )
  },

  exportAnnouncementTaskReflectionsConfig(
    id: number,
    exportAnnouncementTaskReflectionsQuery?: ExportAnnouncementTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/announcement/activity-tasks/{id}/reflections/export',
      {
        id,
      },
      exportAnnouncementTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】公告-导出任务完成数据
   * @param id
   * @param [exportAnnouncementTaskReflectionsQuery]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.nameIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.nameLike]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.accountLike]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.accountIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.genderIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.statusIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.phoneIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.phoneLike]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.emailIn]
   * @param [exportAnnouncementTaskReflectionsQuery.reflection.emailLike]
   * @param [exportAnnouncementTaskReflectionsQuery.endAtEgt] 任务-完成时间-大于
   * @param [exportAnnouncementTaskReflectionsQuery.endAtElt] 任务-完成时间-小于
   * @param [exportAnnouncementTaskReflectionsQuery.filename] 不带后缀文件名
   * @param [exportAnnouncementTaskReflectionsQuery.ridIn]
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportAnnouncementTaskReflections(
    id: number,
    exportAnnouncementTaskReflectionsQuery?: ExportAnnouncementTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      announcementApi.exportAnnouncementTaskReflectionsConfig(
        id,
        exportAnnouncementTaskReflectionsQuery,
        options
      )
    ) as any
  },

  useExportAnnouncementTaskReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportAnnouncementTaskReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      announcementApi.exportAnnouncementTaskReflections,
      'Announcement.exportAnnouncementTaskReflections',
      queryOptions,
      id,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(announcementApi).forEach((api) => {
  // @ts-ignore
  announcementApi[api]._name_ = `Announcement.${api}`
})
